import { DateFilter, MetricData } from '@chiroup/core/types/AdminMetrics.type';
import { AnimatePresence, motion } from 'framer-motion';
import { Activity, Building, Target, TrendingUp, Users } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  ACQUISITION_METRICS,
  HEALTH_METRICS,
  SUBSCRIBER_METRICS,
} from '../../../constants/metrics.constants';
import { cn } from '../../../lib/utils';
import MetricInfoTooltip from './MetricInfoTooltip';
import ModernDatePicker from './ModernDatePicker';

// Pure functions moved outside component to prevent recreation on every render
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;

  return num.toLocaleString();
};

const getCurrentTime = (): string => {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

interface ExecutiveOverviewProps {
  metrics: MetricData[];
  dateFilter: DateFilter;
  onDateFilterChange: (filter: DateFilter) => void;
}

const ExecutiveOverview: React.FC<ExecutiveOverviewProps> = ({
  metrics,
  dateFilter,
  onDateFilterChange,
}) => {
  // Convert metrics array to object for direct access instead of O(n) find operations
  const metricsMap = useMemo(() => {
    const map: Record<string, MetricData> = {};
    metrics.forEach(metric => {
      map[metric.id] = metric;
    });

    return map;
  }, [metrics]);

  // Optimized metric value getter - O(1) lookup instead of O(n) find
  const getMetricValue = (id: string): number => {
    const metric = metricsMap[id];

    return typeof metric?.value === 'number' ? metric.value : 0;
  };

  // Full metric lookup for info tooltips - O(1) via metricsMap
  const getMetric = (id: string): MetricData | undefined => metricsMap[id];

  // Optimized health score getter - O(1) lookup instead of O(n) find
  const getHealthScore = (id: string) => {
    const metric = metricsMap[id];

    if (metric?.displayType === 'object' && typeof metric.value === 'object') {
      const healthData = metric.value as { green: number; yellow: number; red: number };
      const healthy = healthData.green + healthData.yellow;
      const total = healthy + healthData.red;

      return {
        healthy,
        total,
        percentage: total > 0 ? (healthy / total) * 100 : 0,
      };
    }

    return { healthy: 0, total: 0, percentage: 0 };
  };

  // Executive metrics data
  const totalLeadsBase = getMetricValue(ACQUISITION_METRICS.TOTAL_LEADS);
  const campaignMembersNewLeads = getMetricValue(ACQUISITION_METRICS.CAMPAIGN_MEMBERS_NEW_LEADS);
  const totalLeads = totalLeadsBase + campaignMembersNewLeads; // Combine both metrics
  const essentialsTrials = getMetricValue(ACQUISITION_METRICS.ESSENTIALS_TRIALS);
  const ehrDemos = getMetricValue(ACQUISITION_METRICS.EHR_DEMOS);
  const essentialsSubscribers = getMetricValue(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL);
  const ehrSubscribers = getMetricValue(SUBSCRIBER_METRICS.EHR_TOTAL);
  const currentActiveEssentials = getMetricValue(SUBSCRIBER_METRICS.CURRENT_ACTIVE_ESSENTIALS);
  const currentActiveEhr = getMetricValue(SUBSCRIBER_METRICS.CURRENT_ACTIVE_EHR);
  // Get combined health scores for essentials (onboarding + post-boarding)
  const essentialsOnboardingHealth = getHealthScore(HEALTH_METRICS.ESSENTIALS_ONBOARDING);
  const essentialsPostBoardingHealth = getHealthScore(HEALTH_METRICS.ESSENTIALS_POST_BOARDING);

  // Calculate combined essentials health (onboarding + post-boarding)
  const combinedHealthy = essentialsOnboardingHealth.healthy + essentialsPostBoardingHealth.healthy;
  const combinedTotal = essentialsOnboardingHealth.total + essentialsPostBoardingHealth.total;
  const combinedPercentage = combinedTotal > 0 ? (combinedHealthy / combinedTotal) * 100 : 0;

  const essentialsHealth = {
    healthy: combinedHealthy,
    total: combinedTotal,
    percentage: combinedPercentage,
  };

  // Calculation verified - combined health metrics working correctly

  const ehrHealth = getHealthScore(HEALTH_METRICS.EHR_SCORE);

  // Date filter options
  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'past7days', label: 'Past 7 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'past90days', label: 'Past 90 Days' },
    { value: 'yearToDate', label: 'Year to Date' },
    { value: 'custom', label: 'Custom' },
  ] as const;

  // Handle date filter change
  const handleFilterChange = (type: DateFilter['type']) => {
    if (type === 'custom') {
      onDateFilterChange({
        type,
        startDate: dateFilter.startDate || new Date().toISOString().split('T')[0],
        endDate: dateFilter.endDate || new Date().toISOString().split('T')[0],
      });
    } else {
      onDateFilterChange({ type });
    }
  };

  // Handle custom date change
  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      onDateFilterChange({
        ...dateFilter,
        [field]: date.toISOString().split('T')[0],
      });
    }
  };

  // Convert date strings to Date objects for DatePicker
  const getDateValue = (dateString?: string): Date | undefined => {
    return dateString ? new Date(dateString + 'T00:00:00') : undefined;
  };

  return (
    <motion.div
      className={cn(
        'relative overflow-hidden rounded-3xl p-8 text-white shadow-2xl mb-8',
        'bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700',
        'border-4 border-emerald-500/20'
      )}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      {/* Enhanced animated background patterns */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full blur-2xl"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Additional floating particles */}
        <motion.div
          className="absolute top-20 right-1/4 w-16 h-16 bg-emerald-300 rounded-full blur-2xl"
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <motion.div
          className="absolute bottom-20 left-1/4 w-12 h-12 bg-teal-300 rounded-full blur-xl"
          animate={{
            y: [0, 15, 0],
            x: [0, -8, 0],
            opacity: [0.05, 0.12, 0.05],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header Section with Filters at Same Level as Live Data */}
        <div className="mb-5">
          {/* Top Row: Live Data Indicator + Date Filters */}
          <motion.div
            className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-4 gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {/* Enhanced Live Data Indicator */}
            <motion.div
              className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-4 py-2 border-2 border-white/50 shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              {/* Animated Status Indicator */}
              <div className="relative mr-3">
                <motion.div
                  className="w-3 h-3 bg-emerald-400 rounded-full shadow-lg"
                  animate={{
                    scale: [1, 1.2, 1],
                    boxShadow: [
                      '0 0 5px rgba(16, 185, 129, 0.5)',
                      '0 0 15px rgba(16, 185, 129, 0.8)',
                      '0 0 5px rgba(16, 185, 129, 0.5)',
                    ],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                {/* Ripple Effect */}
                <motion.div
                  className="absolute inset-0 bg-emerald-400 rounded-full"
                  animate={{
                    scale: [1, 2.5],
                    opacity: [0.6, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut',
                  }}
                />
              </div>

              {/* Live Data Text */}
              <div className="flex items-center">
                <motion.span
                  className="text-sm font-bold tracking-wider text-white drop-shadow-sm"
                  animate={{ opacity: [0.9, 1, 0.9] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  LIVE DATA
                </motion.span>
                <span className="text-white/80 mx-2 text-xs">•</span>
                <motion.span
                  className="text-xs font-medium text-white/90 tracking-wide"
                  key={getCurrentTime()} // Re-trigger animation on time update
                  initial={{ opacity: 0, y: 2 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Updated {getCurrentTime()}
                </motion.span>
              </div>
            </motion.div>

            {/* Enhanced Date Range Selector - Same Level */}
            <motion.div
              className="bg-white/10 backdrop-blur-sm rounded-xl p-2 border-2 border-white/50"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              {/* Filter Buttons */}
              <div className="flex gap-1 justify-center">
                {dateOptions.map(option => (
                  <motion.button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 whitespace-nowrap',
                      'border-2 focus:outline-none focus:ring-1 focus:ring-emerald-400',
                      dateFilter.type === option.value
                        ? 'bg-white text-emerald-700 hover:bg-white/95 shadow-md border-emerald-300'
                        : 'text-white hover:bg-white/20 hover:text-green-100 border-white/50'
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {option.label}
                  </motion.button>
                ))}
              </div>

              {/* Advanced Custom Date Pickers - Below */}
              <AnimatePresence>
                {dateFilter.type === 'custom' && (
                  <motion.div
                    className="mt-3 pt-3 border-t border-white/20"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/80 font-medium">Start Date</span>
                        <ModernDatePicker
                          selected={getDateValue(dateFilter.startDate)}
                          onChange={date => handleDateChange('startDate', date)}
                          placeholder="MM/DD/YYYY"
                          maxDate={new Date()}
                          align="start"
                          calendarType="start-date"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/80 font-medium">End Date</span>
                        <ModernDatePicker
                          selected={getDateValue(dateFilter.endDate)}
                          onChange={date => handleDateChange('endDate', date)}
                          placeholder="MM/DD/YYYY"
                          minDate={getDateValue(dateFilter.startDate)}
                          maxDate={new Date()}
                          align="start"
                          calendarType="end-date"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Column 1: Lead Generation - Modernized */}
          <motion.div
            className={cn(
              'group relative bg-white/15 backdrop-blur-md rounded-xl p-4 border border-white/30',
              'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
              'hover:shadow-lg cursor-pointer overflow-hidden',
              'focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/70'
            )}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            role="button"
            tabIndex={0}
            aria-label={`New leads: ${formatNumber(totalLeads)} total leads generated in the selected period`}
          >
            {/* Subtle gradient overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="relative z-10">
              {/* Icon and badge section */}
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                  <Users className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
                <div className="flex items-center space-x-1 px-2 py-0.5 rounded-full bg-green-400/20 border border-green-300/30">
                  <TrendingUp className="w-3 h-3 text-green-200" />
                  <span className="text-xs font-bold text-green-100">Active</span>
                </div>
              </div>

              {/* Main value */}
              <div className="mb-2">
                <div className="text-3xl font-black text-white drop-shadow-lg tracking-tight mb-1">
                  {formatNumber(totalLeads)}
                </div>
                <div className="text-sm font-semibold text-white/90 mb-0.5">New Leads</div>
                <div className="text-xs text-white/70 font-medium">Lead Generation Pipeline</div>
              </div>

              {/* Enhanced micro chart bars */}
              <div className="flex justify-end mt-3 space-x-1 opacity-40 group-hover:opacity-60 transition-opacity duration-300">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 bg-white rounded-full h-auto shadow-sm"
                    animate={{
                      height: [
                        Math.random() * 20 + 10,
                        Math.random() * 28 + 14,
                        Math.random() * 20 + 10,
                      ],
                    }}
                    transition={{
                      duration: 2 + i * 0.3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Column 2: Conversion Activities - Modernized */}
          <div className="space-y-2">
            {/* Essentials Trials */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-400/20 backdrop-blur-sm flex items-center justify-center border border-emerald-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Target className="w-4 h-4 text-emerald-200 drop-shadow-lg" />
                  </div>
                  <span className="text-xs text-emerald-200/90 font-semibold px-1.5 py-0.5 bg-emerald-400/10 rounded-full">
                    Trials
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  {formatNumber(essentialsTrials)}
                </div>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/80">
                  Essentials Trials
                  <MetricInfoTooltip
                    info={getMetric(ACQUISITION_METRICS.ESSENTIALS_TRIALS)?.info}
                    query={getMetric(ACQUISITION_METRICS.ESSENTIALS_TRIALS)?.query}
                    className="text-white/70 hover:text-white"
                  />
                </div>
              </div>
            </motion.div>

            {/* EHR Demos */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-400/20 backdrop-blur-sm flex items-center justify-center border border-purple-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Building className="w-4 h-4 text-purple-200 drop-shadow-lg" />
                  </div>
                  <span className="text-xs text-purple-200/90 font-semibold px-1.5 py-0.5 bg-purple-400/10 rounded-full">
                    Demos
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  {formatNumber(ehrDemos)}
                </div>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/80">
                  EHR Demos
                  <MetricInfoTooltip
                    info={getMetric(ACQUISITION_METRICS.EHR_DEMOS)?.info}
                    query={getMetric(ACQUISITION_METRICS.EHR_DEMOS)?.query}
                    className="text-white/70 hover:text-white"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Column 3: New Subscribers - Modernized */}
          <div className="space-y-2">
            {/* New Essentials */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-green-400/20 backdrop-blur-sm flex items-center justify-center border border-green-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Users className="w-4 h-4 text-green-200 drop-shadow-lg" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-green-200/90 font-bold px-1.5 py-0.5 bg-green-400/10 rounded-full">
                    {currentActiveEssentials.toLocaleString()} Active
                    <MetricInfoTooltip
                      info={getMetric(SUBSCRIBER_METRICS.CURRENT_ACTIVE_ESSENTIALS)?.info}
                      query={getMetric(SUBSCRIBER_METRICS.CURRENT_ACTIVE_ESSENTIALS)?.query}
                      className="text-white/70 hover:text-white"
                    />
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  +{essentialsSubscribers.toLocaleString()}
                </div>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/80">
                  New Essentials
                  <MetricInfoTooltip
                    info={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL)?.info}
                    query={getMetric(SUBSCRIBER_METRICS.ESSENTIALS_TOTAL)?.query}
                    className="text-white/70 hover:text-white"
                  />
                </div>
              </div>
            </motion.div>

            {/* New EHR */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-400/20 backdrop-blur-sm flex items-center justify-center border border-purple-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Users className="w-4 h-4 text-purple-200 drop-shadow-lg" />
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-purple-200/90 font-bold px-1.5 py-0.5 bg-purple-400/10 rounded-full">
                    {currentActiveEhr.toLocaleString()} Active
                    <MetricInfoTooltip
                      info={getMetric(SUBSCRIBER_METRICS.CURRENT_ACTIVE_EHR)?.info}
                      query={getMetric(SUBSCRIBER_METRICS.CURRENT_ACTIVE_EHR)?.query}
                      className="text-white/70 hover:text-white"
                    />
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  +{ehrSubscribers.toLocaleString()}
                </div>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/80">
                  New EHR
                  <MetricInfoTooltip
                    info={getMetric(SUBSCRIBER_METRICS.EHR_TOTAL)?.info}
                    query={getMetric(SUBSCRIBER_METRICS.EHR_TOTAL)?.query}
                    className="text-white/70 hover:text-white"
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Column 4: Health Metrics - Modernized */}
          <div className="space-y-2">
            {/* Healthy Essentials */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.2, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-green-400/20 backdrop-blur-sm flex items-center justify-center border border-green-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Activity className="w-4 h-4 text-green-200 drop-shadow-lg" />
                  </div>
                  <span className="text-xs text-green-200/90 font-bold px-1.5 py-0.5 bg-green-400/10 rounded-full">
                    {formatNumber(essentialsHealth.healthy)} Total
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  {essentialsHealth.percentage.toFixed(1)}%
                </div>
                <div className="text-xs font-semibold text-white/80">Essentials Healthy</div>
              </div>
            </motion.div>

            {/* Healthy EHR */}
            <motion.div
              className={cn(
                'group relative bg-white/15 backdrop-blur-md rounded-lg p-3 border border-white/30',
                'hover:bg-white/20 hover:border-white/50 transition-all duration-300',
                'hover:shadow-lg cursor-pointer overflow-hidden'
              )}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.4, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-400/20 backdrop-blur-sm flex items-center justify-center border border-purple-300/30 shadow-md group-hover:scale-105 transition-transform duration-200">
                    <Activity className="w-4 h-4 text-purple-200 drop-shadow-lg" />
                  </div>
                  <span className="text-xs text-purple-200/90 font-bold px-1.5 py-0.5 bg-purple-400/10 rounded-full">
                    {formatNumber(ehrHealth.healthy)} Total
                  </span>
                </div>
                <div className="text-xl font-black text-white drop-shadow-lg tracking-tight mb-0.5">
                  {ehrHealth.percentage.toFixed(1)}%
                </div>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/80">
                  EHR Healthy
                  <MetricInfoTooltip
                    info={getMetric(HEALTH_METRICS.EHR_SCORE)?.info}
                    query={getMetric(HEALTH_METRICS.EHR_SCORE)?.query}
                    className="text-white/70 hover:text-white"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ExecutiveOverview;
